import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import type {SelectMenuOption} from 'shells/action-menu/action-menu';
import {Dialog} from 'shells/dialog/dialog';
import {EmailRowItemCollapsed} from 'shells/email-row-item/email-row-item-collapsed';
import {EmailRowItemError} from 'shells/email-row-item/email-row-item-error';
import {EmailRowItemExpanded} from 'shells/email-row-item/email-row-item-expanded';
import {EmailRowItemLoading} from 'shells/email-row-item/email-row-item-loading';
import type {SequenceInfo, EmailListItem, EmailStatus} from 'shells/email-row-item/types';

import {GetEmailDetails} from './get-email-details';
import {sortAddresseesByEntityType} from './sort-addresses-by-entity-type';

type Props = {
    sequenceInfo?: SequenceInfo | null | undefined;
    emailListItem: EmailListItem;
    isQueued?: boolean;
    isExpanded: boolean;
    status?: EmailStatus;
    isDeletingEmail: boolean;
    deleteQuestion: string;
    onDeleteConfirm: (e: React.SyntheticEvent<any>) => any;
    onDeleteCancel: (e: React.SyntheticEvent<any>) => any;
    actions: SelectMenuOption[];
    navToSequenceDetails?: () => void;
    detailLinkComponent?: React.ReactNode;
    // Can't use CSS psuedo selectors here (without some hacks)
    // so we'll pass in a prop to toggle our top-border if this
    // row is the first of its date.
    isFirstOfDate: boolean;
    // This should be true if the row item is an incoming email, rather than
    // outgoing. This switches around the to and from components
    isReceivedEmail: boolean;
};

type State = {
    isExpanded: boolean;
};

/**
 * The representation of an email within a Nutshell Timeline.
 */
export class EmailRow extends React.PureComponent<Props, State> {
    delayButton: HTMLElement | null | undefined;
    actionMenu: HTMLElement | null | undefined;

    static defaultProps = {
        deleteQuestion: 'Are you sure you want to delete this email?',
        isExpanded: false,
        isReceivedEmail: false,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            isExpanded: props.isExpanded,
        };
    }

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        const {emailListItem, isQueued} = this.props;

        const deletingDialog =
            this.props.isDeletingEmail && this.actionMenu ? (
                <Dialog
                    anchor={this.actionMenu}
                    question={this.props.deleteQuestion}
                    isWarning={true}
                    onPrimary={this.props.onDeleteConfirm}
                    onSecondary={this.props.onDeleteCancel}
                />
            ) : null;

        if (this.state.isExpanded) {
            return (
                <GetEmailDetails emailId={emailListItem.id} isQueued={Boolean(isQueued)}>
                    {({emailMessage, isLoading, refetch}) => {
                        if (isLoading) {
                            return <EmailRowItemLoading />;
                        }

                        if (!emailMessage) {
                            return <EmailRowItemError retry={refetch} />;
                        }

                        return (
                            <EmailRowItemExpanded
                                sequenceInfo={this.props.sequenceInfo}
                                email={emailMessage}
                                getRef={(node) => {
                                    this.actionMenu = node;
                                }}
                                status={this.props.status}
                                actions={this.props.actions}
                                onCollapseRow={this.handleClick}
                                deletingDialog={deletingDialog}
                                detailLinkComponent={this.props.detailLinkComponent}
                                navToSequenceDetails={this.props.navToSequenceDetails}
                                isFirstOfDate={this.props.isFirstOfDate}
                                // @ts-expect-error
                                isReceivedEmail={this.props.isReceivedEmail}
                            />
                        );
                    }}
                </GetEmailDetails>
            );
        }

        const emailWithSortedRecipients = {
            // @ts-expect-error
            to: sortAddresseesByEntityType(emailListItem.to),
            ...emailListItem,
        } as const;

        return (
            <EmailRowItemCollapsed
                sequenceInfo={this.props.sequenceInfo}
                email={emailWithSortedRecipients}
                getRef={(node) => {
                    this.actionMenu = node;
                }}
                status={this.props.status}
                actions={this.props.actions}
                onExpandRow={this.handleClick}
                deletingDialog={deletingDialog}
                navToSequenceDetails={this.props.navToSequenceDetails}
                isFirstOfDate={this.props.isFirstOfDate}
                isReceivedEmail={this.props.isReceivedEmail}
            />
        );
    }

    handleClick = () => {
        this.setState((prevState: State) => ({
            isExpanded: !prevState.isExpanded,
        }));
    };
}
